import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Building2, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/layout/page-header";
import { Segmented } from "@/components/layout/segmented";
import { EmptyState, NadaEncontrado } from "@/components/data/empty-state";
import { estadoDaConsulta } from "@/components/data/estado-consulta";
import { StatusChip } from "@/components/mercado/status-chip";
import { StackTable, StackRow, StackCell } from "@/components/mercado/stack-table";
import { useAuth } from "@/lib/auth-context";
import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { usePracas } from "@/hooks/use-cotacoes";
import { useAtualizarOrganizacao, useCriarOrganizacao, useOrganizacoes } from "@/hooks/use-organizacoes";
import { fmtDataCurtaBR } from "@/lib/formato-mercado";
import { telefoneWhatsappValido } from "@shared/mercado";
import { labelPerfilComprador } from "@shared/schema";
import {
  filtrarOrganizacoes, labelOrgTipo, modulosPadraoDoTipo,
  type OrganizacaoDTO,
} from "@shared/plataforma-organizacoes";

/**
 * Plataforma › Organizações — pré-cadastrar quem ainda não tem conta.
 *
 * ═══ O QUE ESTA TELA NÃO FAZ ════════════════════════════════════════════════
 *
 * **Não cria conta nem senha.** O usuário nasce sempre do claim, pelo link que
 * prova posse: `/v/` (romaneio), `/o/` (oferta), `/c/` (carga). Criar credencial
 * no lugar de outra pessoa produz organização que ninguém reivindicou e senha que
 * alguém precisa transmitir por WhatsApp.
 *
 * **Não gera link de convite genérico.** Todo comprador já tem um link com
 * conteúdo — o corretor recebe romaneio, o distribuidor recebe carga. Um convite
 * vazio é um primeiro toque pior que um documento, e é o oposto de "valor antes do
 * cadastro", que é o que fez o C1 funcionar.
 *
 * O que ela faz é **pré-cadastrar a organização, sem usuário**: hoje um comprador
 * só existe se alguém formalizou um romaneio para ele, e "toda a praça de Mirorós
 * (11 compradores)" só é verdade se os 11 existirem antes de qualquer um ter conta.
 *
 * ⚠️ "Sem acesso" é DERIVADO — organização com zero usuários. Não existe coluna.
 */

// ⚠️ Constantes de MÓDULO (deps de useMemo dentro do hook) — ver estado-na-url.
const PADRAO = { q: "", tipo: "todos" };
const PERMITIDOS = { tipo: ["todos", "PRODUTOR", "COMPRADOR"] } as const;

const COLUNAS = [
  { label: "Organização", w: true },
  { label: "Cidade" },
  { label: "WhatsApp" },
  { label: "Praças" },
  { label: "Acesso" },
  { label: "Criada" },
  { label: "" },
];

export default function AdminOrganizacoesPage() {
  const { user } = useAuth();
  const ehPlataforma = !!user?.plataformaAdmin;

  const { estado, definir } = useEstadoNaUrl(PADRAO, PERMITIDOS);
  const consulta = useOrganizacoes(ehPlataforma);
  const aguardando = estadoDaConsulta(consulta, { variante: "tabela" });

  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<OrganizacaoDTO | null>(null);

  const filtradas = useMemo(
    () => filtrarOrganizacoes(consulta.data ?? [], { q: estado.q, tipo: estado.tipo }),
    [consulta.data, estado.q, estado.tipo],
  );

  if (!ehPlataforma) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Acesso restrito ao administrador da plataforma.
      </div>
    );
  }

  const semAcesso = (consulta.data ?? []).filter(o => o.usuarios === 0).length;

  return (
    <div className="p-4 md:p-6 space-y-4">
      <PageHeader
        titulo="Organizações"
        descricao="Pré-cadastre quem ainda não tem conta. O acesso nasce quando a pessoa abrir um link do Rizoma."
        empilharQuandoEstreito
        acao={
          <Button onClick={() => setCriando(true)} data-testid="button-nova-organizacao">
            <Plus className="h-4 w-4 mr-1" />Nova organização
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={estado.q}
          onChange={e => definir({ q: e.target.value })}
          placeholder="Buscar por nome ou telefone"
          className="max-w-xs"
          data-testid="input-busca-organizacao"
        />
        <Segmented
          valor={estado.tipo}
          onChange={v => definir({ tipo: v })}
          testId="seg-tipo-organizacao"
          opcoes={[
            { id: "todos", label: "Todas" },
            { id: "PRODUTOR", label: "Produtores" },
            { id: "COMPRADOR", label: "Compradores" },
          ]}
        />
        {semAcesso > 0 && (
          <span className="text-xs text-muted-foreground" data-testid="resumo-sem-acesso">
            {semAcesso === 1 ? "1 aguardando o primeiro acesso" : `${semAcesso} aguardando o primeiro acesso`}
          </span>
        )}
      </div>

      {aguardando ?? (
        filtradas.length === 0 ? (
          estado.q.trim() ? <NadaEncontrado termo={estado.q.trim()} /> : (
            <EmptyState
              icone={Building2}
              titulo="Nenhuma organização"
              descricao="Crie a primeira para que ela exista na praça antes de alguém ter conta."
            />
          )
        ) : (
          <StackTable colunas={COLUNAS} larguraMinima={860} testId="tabela-organizacoes">
            {filtradas.map(o => (
              <StackRow key={o.id} testId={`linha-org-${o.slug}`}>
                <StackCell w label="Organização">
                  <div className="min-w-0">
                    <p className="font-medium truncate">
                      {o.nome}
                      {o.nomeFantasia && <span className="text-muted-foreground"> · {o.nomeFantasia}</span>}
                    </p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap mt-0.5">
                      <StatusChip kind={o.tipo === "COMPRADOR" ? "inf" : "conf"}>
                        {labelOrgTipo(o.tipo)}
                      </StatusChip>
                      {o.tipo === "COMPRADOR" && o.perfilComprador && (
                        <StatusChip kind="vista">{labelPerfilComprador(o.perfilComprador)}</StatusChip>
                      )}
                      <span className="font-mono">{o.slug}</span>
                    </p>
                  </div>
                </StackCell>
                <StackCell label="Cidade">{o.cidade ?? <span className="text-muted-foreground">—</span>}</StackCell>
                <StackCell label="WhatsApp">
                  {o.telefoneWhatsapp
                    ? <span className="tabular-nums">{o.telefoneWhatsapp}</span>
                    : <span className="text-muted-foreground">—</span>}
                </StackCell>
                <StackCell label="Praças">
                  {o.pracas.length > 0
                    ? <span className="text-xs">{o.pracas.join(", ")}</span>
                    : <span className="text-muted-foreground">—</span>}
                </StackCell>
                <StackCell label="Acesso">
                  {/* ⚠️ "sem acesso" é a informação que faz esta tela valer: é a
                      organização esperando alguém reivindicá-la. */}
                  {o.usuarios === 0
                    ? <StatusChip kind="warn" testId={`chip-sem-acesso-${o.slug}`}>sem acesso</StatusChip>
                    : <span className="text-xs">{o.usuarios} {o.usuarios === 1 ? "usuário" : "usuários"}</span>}
                </StackCell>
                <StackCell label="Criada">{fmtDataCurtaBR(o.criadoEm.substring(0, 10))}</StackCell>
                <StackCell acts label="">
                  <Button variant="outline" size="sm" onClick={() => setEditando(o)} data-testid={`button-editar-${o.slug}`}>
                    Editar
                  </Button>
                </StackCell>
              </StackRow>
            ))}
          </StackTable>
        )
      )}

      {criando && <DialogNova onFechar={() => setCriando(false)} />}
      {editando && <DialogEditar org={editando} onFechar={() => setEditando(null)} />}
    </div>
  );
}

/** Os campos que as duas folhas compartilham. */
function CamposComuns({
  nome, setNome, fantasia, setFantasia, cidade, setCidade, telefone, setTelefone,
  pracas, alternarPraca, modulos, alternarModulo,
}: {
  nome: string; setNome: (v: string) => void;
  fantasia: string; setFantasia: (v: string) => void;
  cidade: string; setCidade: (v: string) => void;
  telefone: string; setTelefone: (v: string) => void;
  pracas: string[]; alternarPraca: (slug: string) => void;
  modulos: string[]; alternarModulo: (id: string) => void;
}) {
  const { data: todasPracas = [] } = usePracas();
  const telefoneOk = !telefone || telefoneWhatsappValido(telefone);

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="org-nome">Nome</Label>
        <Input id="org-nome" value={nome} onChange={e => setNome(e.target.value)} placeholder="Sávio Nunes" data-testid="input-org-nome" />
      </div>

      <div className="space-y-2">
        <Label htmlFor="org-fantasia">Nome fantasia <span className="text-muted-foreground">(opcional)</span></Label>
        <Input id="org-fantasia" value={fantasia} onChange={e => setFantasia(e.target.value)} data-testid="input-org-fantasia" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="org-telefone">WhatsApp</Label>
          <Input
            id="org-telefone" inputMode="tel" value={telefone}
            onChange={e => setTelefone(e.target.value)}
            placeholder="74 99999-8888"
            data-testid="input-org-telefone"
          />
          {/* ⚠️ É O TELEFONE QUE LIGA O PRÉ-CADASTRO AO CLAIM: é por ele que o
              link enviado encontra ESTA organização em vez de criar uma segunda.
              Errar o número não dá erro nenhum — só produz dois cadastros. */}
          {!telefoneOk && (
            <p className="text-xs text-destructive" data-testid="erro-telefone">
              DDD + 9 + oito dígitos.
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            É por este número que o link vai encontrar esta organização.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="org-cidade">Cidade <span className="text-muted-foreground">(opcional)</span></Label>
          <Input id="org-cidade" value={cidade} onChange={e => setCidade(e.target.value)} placeholder="Irecê" data-testid="input-org-cidade" />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Praças</Label>
        <div className="flex flex-wrap gap-3" data-testid="lista-pracas-org">
          {todasPracas.length === 0
            ? <p className="text-xs text-muted-foreground">Nenhuma praça ativa cadastrada.</p>
            : todasPracas.map(p => (
              <label key={p.slug} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={pracas.indexOf(p.slug) >= 0}
                  onCheckedChange={() => alternarPraca(p.slug)}
                  data-testid={`check-praca-${p.slug}`}
                />
                {p.nome}
              </label>
            ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Módulos</Label>
        <div className="flex flex-wrap gap-3">
          {["cacho", "equipe", "manejo", "mercado"].map(id => (
            <label key={id} className="flex items-center gap-2 text-sm capitalize">
              <Checkbox
                checked={modulos.indexOf(id) >= 0}
                onCheckedChange={() => alternarModulo(id)}
                data-testid={`check-modulo-${id}`}
              />
              {id}
            </label>
          ))}
        </div>
      </div>
    </>
  );
}

function DialogNova({ onFechar }: { onFechar: () => void }) {
  const { toast } = useToast();
  const criar = useCriarOrganizacao();

  const [nome, setNome] = useState("");
  const [fantasia, setFantasia] = useState("");
  const [cidade, setCidade] = useState("");
  const [telefone, setTelefone] = useState("");
  const [tipo, setTipo] = useState("COMPRADOR");
  const [perfil, setPerfil] = useState("CORRETOR");
  const [pracas, setPracas] = useState<string[]>([]);
  const [modulos, setModulos] = useState<string[]>(modulosPadraoDoTipo("COMPRADOR"));

  function trocarTipo(novo: string) {
    setTipo(novo);
    // Os módulos acompanham o tipo enquanto ninguém mexeu neles à mão: comprador
    // com Cacho no menu é tela que responde 403.
    setModulos(modulosPadraoDoTipo(novo));
  }

  const podeSalvar = nome.trim().length >= 2 && telefoneWhatsappValido(telefone) && !criar.isPending;

  return (
    <Dialog open onOpenChange={o => !o && onFechar()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nova organização</DialogTitle>
          <DialogDescription>
            Sem conta e sem senha. A conta é criada quando a pessoa abrir um link do Rizoma —
            um romaneio, uma oferta ou uma carga — com este telefone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Tipo</Label>
            <Segmented
              valor={tipo}
              onChange={trocarTipo}
              testId="seg-novo-tipo"
              className="w-full [&>button]:flex-1"
              opcoes={[
                { id: "COMPRADOR", label: "Comprador" },
                { id: "PRODUTOR", label: "Produtor" },
              ]}
            />
          </div>

          {/* ⚠️ Perfil é OBRIGATÓRIO no comprador, como no claim: corretor e
              distribuidor são negócios diferentes, e adivinhar carimbaria metade
              da praça errada no cadastro de quem não tem como corrigir. */}
          {tipo === "COMPRADOR" && (
            <div className="space-y-2">
              <Label>Perfil</Label>
              <Segmented
                valor={perfil}
                onChange={setPerfil}
                testId="seg-novo-perfil"
                className="w-full [&>button]:flex-1"
                opcoes={[
                  { id: "CORRETOR", label: "Corretor" },
                  { id: "DISTRIBUIDOR", label: "Distribuidor" },
                ]}
              />
            </div>
          )}

          <CamposComuns
            nome={nome} setNome={setNome}
            fantasia={fantasia} setFantasia={setFantasia}
            cidade={cidade} setCidade={setCidade}
            telefone={telefone} setTelefone={setTelefone}
            pracas={pracas}
            alternarPraca={slug => setPracas(a => a.indexOf(slug) >= 0 ? a.filter(x => x !== slug) : a.concat([slug]))}
            modulos={modulos}
            alternarModulo={id => setModulos(a => a.indexOf(id) >= 0 ? a.filter(x => x !== id) : a.concat([id]))}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onFechar}>Cancelar</Button>
          <Button
            onClick={() => criar.mutate(
              {
                nome, tipo,
                perfilComprador: tipo === "COMPRADOR" ? perfil : null,
                telefoneWhatsapp: telefone,
                cidade: cidade || null,
                nomeFantasia: fantasia || null,
                pracas, modulesEnabled: modulos,
              },
              {
                onSuccess: org => {
                  onFechar();
                  toast({
                    title: `${org.nome} pré-cadastrada.`,
                    description: "Ela aparece como “sem acesso” até alguém abrir um link com este telefone.",
                  });
                },
                // Nunca engolir: sem isto o admin toca em Criar, nada acontece, e
                // ele tenta de novo criando duplicata quando o erro era outro.
                onError: (e: Error) => toast({ title: "Não deu para criar", description: e.message, variant: "destructive" }),
              },
            )}
            disabled={!podeSalvar}
            data-testid="button-salvar-organizacao"
          >
            {criar.isPending ? "Criando…" : "Criar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Editar — é assim que se corrige o comprador que reivindicou como corretor e é
 * distribuidor.
 *
 * ⚠️ `tipo` NÃO se edita. Virar PRODUTOR uma org COMPRADOR com romaneios
 * recebidos deixaria histórico apontando para um lado do balcão que ela já não
 * ocupa, e nenhuma tela sabe ler isso.
 */
function DialogEditar({ org, onFechar }: { org: OrganizacaoDTO; onFechar: () => void }) {
  const { toast } = useToast();
  const atualizar = useAtualizarOrganizacao();

  const [nome, setNome] = useState(org.nome);
  const [fantasia, setFantasia] = useState(org.nomeFantasia ?? "");
  const [cidade, setCidade] = useState(org.cidade ?? "");
  const [telefone, setTelefone] = useState(org.telefoneWhatsapp ?? "");
  const [perfil, setPerfil] = useState(org.perfilComprador ?? "CORRETOR");
  const [pracas, setPracas] = useState<string[]>(org.pracas);
  const [modulos, setModulos] = useState<string[]>(org.modulesEnabled);

  const podeSalvar = nome.trim().length >= 2
    && (!telefone || telefoneWhatsappValido(telefone))
    && !atualizar.isPending;

  return (
    <Dialog open onOpenChange={o => !o && onFechar()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{org.nome}</DialogTitle>
          <DialogDescription>
            {labelOrgTipo(org.tipo)} · {org.usuarios === 0
              ? "sem acesso — esperando alguém abrir um link com este telefone"
              : `${org.usuarios} ${org.usuarios === 1 ? "usuário" : "usuários"}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {org.tipo === "COMPRADOR" && (
            <div className="space-y-2">
              <Label>Perfil</Label>
              <Segmented
                valor={perfil}
                onChange={setPerfil}
                testId="seg-editar-perfil"
                className="w-full [&>button]:flex-1"
                opcoes={[
                  { id: "CORRETOR", label: "Corretor" },
                  { id: "DISTRIBUIDOR", label: "Distribuidor" },
                ]}
              />
            </div>
          )}

          <CamposComuns
            nome={nome} setNome={setNome}
            fantasia={fantasia} setFantasia={setFantasia}
            cidade={cidade} setCidade={setCidade}
            telefone={telefone} setTelefone={setTelefone}
            pracas={pracas}
            alternarPraca={slug => setPracas(a => a.indexOf(slug) >= 0 ? a.filter(x => x !== slug) : a.concat([slug]))}
            modulos={modulos}
            alternarModulo={id => setModulos(a => a.indexOf(id) >= 0 ? a.filter(x => x !== id) : a.concat([id]))}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onFechar}>Cancelar</Button>
          <Button
            onClick={() => atualizar.mutate(
              {
                id: org.id, nome,
                nomeFantasia: fantasia || null,
                cidade: cidade || null,
                telefoneWhatsapp: telefone || null,
                perfilComprador: org.tipo === "COMPRADOR" ? perfil : undefined,
                pracas, modulesEnabled: modulos,
              },
              {
                onSuccess: () => { onFechar(); toast({ title: "Cadastro atualizado." }); },
                onError: (e: Error) => toast({ title: "Não deu para salvar", description: e.message, variant: "destructive" }),
              },
            )}
            disabled={!podeSalvar}
            data-testid="button-salvar-edicao-organizacao"
          >
            {atualizar.isPending ? "Salvando…" : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
